/**
 * Discount Configuration Utility
 * Centralized discount logic using environment variables
 */

// Get discount percentages from environment variables
export const BIANNUAL_DISCOUNT_PERCENT = Number(import.meta.env.VITE_BIANNUAL_DISCOUNT) || 10;
export const ANNUAL_DISCOUNT_PERCENT = Number(import.meta.env.VITE_ANNUAL_DISCOUNT) || 25;

// Convert percentages to decimal multipliers
export const BIANNUAL_DISCOUNT_MULTIPLIER = BIANNUAL_DISCOUNT_PERCENT / 100; // 0.1
export const ANNUAL_DISCOUNT_MULTIPLIER = ANNUAL_DISCOUNT_PERCENT / 100; // 0.25

// Calculate the price multiplier after discount (what customer pays)
export const BIANNUAL_PRICE_MULTIPLIER = 1 - BIANNUAL_DISCOUNT_MULTIPLIER; // 0.9
export const ANNUAL_PRICE_MULTIPLIER = 1 - ANNUAL_DISCOUNT_MULTIPLIER; // 0.75

/**
 * Get discount percentage for a billing period
 */
export const getDiscountPercent = (period: 'monthly' | 'quarterly' | 'biannual' | 'annual'): number => {
  switch (period) {
    case 'biannual':
      return BIANNUAL_DISCOUNT_PERCENT;
    case 'annual':
      return ANNUAL_DISCOUNT_PERCENT;
    default:
      return 0;
  }
};

/**
 * Get discount multiplier for a billing period
 */
export const getDiscountMultiplier = (period: 'monthly' | 'quarterly' | 'biannual' | 'annual'): number => {
  switch (period) {
    case 'biannual':
      return BIANNUAL_DISCOUNT_MULTIPLIER;
    case 'annual':
      return ANNUAL_DISCOUNT_MULTIPLIER;
    default:
      return 0;
  }
};

/**
 * Get price multiplier after discount
 */
export const getPriceMultiplier = (period: 'monthly' | 'quarterly' | 'biannual' | 'annual'): number => {
  switch (period) {
    case 'biannual':
      return BIANNUAL_PRICE_MULTIPLIER;
    case 'annual':
      return ANNUAL_PRICE_MULTIPLIER;
    default:
      return 1;
  }
};

/**
 * Calculate savings amount
 */
export const calculateSavings = (basePrice: number, period: 'monthly' | 'quarterly' | 'biannual' | 'annual'): number => {
  const months = period === 'biannual' ? 6 : period === 'annual' ? 12 : 0;
  const discount = getDiscountMultiplier(period);
  return Math.round(basePrice * months * discount);
};

/**
 * Get savings text for display
 */
export const getSavingsText = (period: 'monthly' | 'quarterly' | 'biannual' | 'annual'): string | null => {
  const discount = getDiscountPercent(period);
  return discount > 0 ? `Save ${discount}%` : null;
};
